import {
  Button,
  CircularProgress,
  FormControl,
  Grid2,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  Box,
  Paper,
  Skeleton,
  alpha,
  useTheme,
  Stack,
  IconButton,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  Visibility,
  FilterList,
  Assignment,
  PictureAsPdf, // Changed Icon for clarity
  ZoomIn,
  ZoomOut,
  RestartAlt,
  Fullscreen,
  FullscreenExit,
  SearchOff,
} from '@mui/icons-material';
import { useEffect, useState, useRef } from 'react';
import { getYears } from '../../../../helpers/dateHelpers';
import {
  getFacilityCategoryReport,
  getFacilityCategoryV2,
  getMeasurements,
  getStatePersonalizedV2,
} from '../../../../helpers/api';
import { openNotification } from '../../../../helpers/notification';

// Import html2pdf
import html2pdf from 'html2pdf.js/dist/html2pdf.bundle.min.js';

export default function CategoryAnalytics() {
  const theme = useTheme();
  const reportRef = useRef(null);

  const quarters = [
    { id: 1, name: 'Quarter 1' },
    { id: 2, name: 'Quarter 2' },
    { id: 3, name: 'Quarter 3' },
    { id: 4, name: 'Quarter 4' },
  ];

  // States
  const [quarterSelected, setQuarterSelected] = useState('');
  const [yearSelected, setYearSelected] = useState('');
  const [htmlContent, setHtmlContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [categoryData, setCategoryData] = useState([]);
  const [categorySelected, setCategorySelected] = useState('');
  const [measurementLoading, setMeasurementLoading] = useState(false);
  const [measurementData, setMeasurementData] = useState([]);
  const [measurementSelected, setMeasurementSelected] = useState('');
  const [stateList, setStateList] = useState([]);
  const [stateLoading, setStateLoading] = useState(false);
  const [stateSelected, setStateSelected] = useState('');
  const [hasResult, setHasResult] = useState(true);

  // View Functionality States
  const [zoom, setZoom] = useState(1);
  const [isFullWidth, setIsFullWidth] = useState(false);

  useEffect(() => {
    fetchCategory();
    fetchMeasurement();
    fetchStateList();
  }, []);

  const fetchCategory = () => {
    setCategoryLoading(true);
    getFacilityCategoryV2()
      .then((response) => {
        const responseData = response?.result?.data || [];
        setCategoryData(responseData);
      })
      .catch((err) => console.error('Failed to fetch categories', err))
      .finally(() => setCategoryLoading(false));
  };

  const fetchMeasurement = () => {
    setMeasurementLoading(true);
    getMeasurements()
      .then((response) => {
        const responseData = response?.result?.data || [];
        setMeasurementData(responseData);
      })
      .catch((err) => console.error('Failed to fetch measurements', err))
      .finally(() => setMeasurementLoading(false));
  };

  const fetchStateList = async () => {
    setStateLoading(true);
    try {
      const res = await getStatePersonalizedV2();
      const { data, status } = res?.result;
      if (status?.code === '00') {
        setStateList(data);
      } else {
        openNotification({ type: 'error', message: status?.desc });
      }
    } catch (error) {
      openNotification({
        type: 'error',
        message: error?.data?.result?.status?.desc || 'Error fetching states.',
      });
    } finally {
      setStateLoading(false);
    }
  };

  const handlePreview = async () => {
    if (!measurementSelected || !categorySelected || !quarterSelected || !yearSelected) {
      alert('Please select all required fields first!');
      return;
    }

    setLoading(true);
    setHasResult(true);
    try {
      const res = await getFacilityCategoryReport(
        categorySelected,
        quarterSelected,
        yearSelected,
        measurementSelected,
        stateSelected
      );
      const { data, status } = res?.result;
      if (status?.code === '00') {
        if (!data || data.trim() === '') {
          setHasResult(false);
          setHtmlContent('');
        } else {
          setHasResult(true);
          setHtmlContent(data);
        }
      } else {
        openNotification({ type: 'error', message: status?.desc });
      }
    } catch (error) {
      openNotification({
        type: 'error',
        message: error?.data?.result?.status?.desc || 'An error occurred',
      });
    } finally {
      setLoading(false);
    }
  };

  /**
   * DIRECT PDF DOWNLOAD LOGIC
   */
  const handleDownloadPDF = () => {
    if (!htmlContent) return;

    const element = reportRef.current;

    // PDF Configuration
    const opt = {
      margin: [10, 10, 10, 10], // top, left, bottom, right
      filename: `Report_${categorySelected}_${yearSelected}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: {
        scale: 2, // Higher scale = better quality
        useCORS: true,
        letterRendering: true,
      },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'landscape' }, // Landscape often better for tables
      pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
    };

    // Execute Download
    html2pdf()
      .set(opt)
      .from(element)
      .save();
  };

  const renderContent = () => {
    if (loading) {
      return (
        <Box sx={{ p: 4, height: '100%' }}>
          <Skeleton variant="text" width="40%" height={40} />
          <Skeleton
            variant="rectangular"
            width="100%"
            height="80%"
            sx={{ mt: 2, borderRadius: 2 }}
          />
        </Box>
      );
    }

    if (!htmlContent) {
      return (
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'text.secondary',
            textAlign: 'center',
            p: 3,
          }}
        >
          {!hasResult ? (
            <>
              <SearchOff
                sx={{ fontSize: 80, mb: 2, color: theme.palette.error.light, opacity: 0.8 }}
              />
              <Typography variant="h5" color="error.main" fontWeight={700}>
                No Records Found
              </Typography>
              <Typography variant="body1" sx={{ mt: 1, maxWidth: 400 }}>
                We couldn't find any data matching your selected filters.
              </Typography>
            </>
          ) : (
            <>
              <Assignment sx={{ fontSize: 80, mb: 2, opacity: 0.1 }} />
              <Typography variant="h6" fontWeight={600}>
                Report Preview Ready
              </Typography>
              <Typography variant="body2">
                Select parameters and click "Preview" to generate the table.
              </Typography>
            </>
          )}
        </Box>
      );
    }

    return (
      <Box
        sx={{
          height: '100%',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: '#f5f5f5',
          overflow: 'hidden',
        }}
      >
        <Paper
          square
          elevation={1}
          sx={{
            p: 1,
            px: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            zIndex: 10,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title="Zoom Out">
              <IconButton size="small" onClick={() => setZoom((prev) => Math.max(prev - 0.1, 0.5))}>
                <ZoomOut />
              </IconButton>
            </Tooltip>
            <Typography
              variant="caption"
              sx={{ minWidth: 40, textAlign: 'center', fontWeight: 700 }}
            >
              {Math.round(zoom * 100)}%
            </Typography>
            <Tooltip title="Zoom In">
              <IconButton size="small" onClick={() => setZoom((prev) => Math.min(prev + 0.1, 1.5))}>
                <ZoomIn />
              </IconButton>
            </Tooltip>
            <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
            <Tooltip title={isFullWidth ? 'Exit Full Width' : 'Full Width View'}>
              <IconButton size="small" onClick={() => setIsFullWidth(!isFullWidth)}>
                {isFullWidth ? <FullscreenExit /> : <Fullscreen />}
              </IconButton>
            </Tooltip>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              startIcon={<RestartAlt />}
              onClick={() => {
                setZoom(1);
                setIsFullWidth(false);
                handlePreview();
              }}
              color="inherit"
              sx={{ textTransform: 'none' }}
            >
              Refresh
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={<PictureAsPdf />}
              onClick={handleDownloadPDF}
              sx={{ textTransform: 'none' }}
            >
              Download
            </Button>
          </Stack>
        </Paper>

        <Box
          sx={{
            flexGrow: 1,
            p: isFullWidth ? 0 : { xs: 1, md: 3 },
            overflow: 'hidden',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Paper
            elevation={isFullWidth ? 0 : 3}
            sx={{
              width: '100%',
              maxWidth: isFullWidth ? '100%' : '1200px',
              height: '100%',
              bgcolor: 'white',
              overflow: 'hidden',
              transition: 'max-width 0.3s ease-in-out',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box
              sx={{
                p: { xs: 2, md: 5 },
                overflow: 'auto',
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <div
                ref={reportRef} // Attached ref here for PDF targeting
                style={{
                  transform: `scale(${zoom})`,
                  transformOrigin: 'top center',
                  width: '100%',
                  maxWidth: '100%',
                }}
                dangerouslySetInnerHTML={{ __html: htmlContent }}
                className="report-html-container"
              />
            </Box>
          </Paper>
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ height: '80vh', bgcolor: 'background.default', overflow: 'hidden' }}>
      <Grid2 container sx={{ height: '100%' }} spacing={0}>
        <Grid2 size={{ lg: 9, md: 8, sm: 12, xs: 12 }} sx={{ height: '100%' }}>
          <Box sx={{ height: '100%', borderRight: 1, borderColor: 'divider' }}>
            {renderContent()}
          </Box>
        </Grid2>

        <Grid2 size={{ lg: 3, md: 4, sm: 12, xs: 12 }} sx={{ height: '100%', bgcolor: 'white' }}>
          <Stack sx={{ height: '100%' }}>
            <Box
              sx={{
                p: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <FilterList color="primary" fontSize="small" />
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  color="primary.main"
                  sx={{ textTransform: 'uppercase' }}
                >
                  Preview Controls
                </Typography>
              </Stack>
            </Box>

            <Box sx={{ p: 3, flexGrow: 1, overflowY: 'auto' }}>
              <Stack spacing={3}>
                <Divider>
                  <Typography color="primary" variant="body2">
                    Mandatory Selection
                  </Typography>
                </Divider>

                <FormControl fullWidth size="small">
                  <InputLabel>Measurement *</InputLabel>
                  <Select
                    size="small"
                    label="Measurement *"
                    value={measurementSelected}
                    onChange={(e) => setMeasurementSelected(e.target.value)}
                    required
                    startAdornment={
                      measurementLoading && <CircularProgress size={20} sx={{ mr: 1 }} />
                    }
                  >
                    {measurementData?.map((m) => (
                      <MenuItem key={m?.measurementId} value={m?.measurementId}>
                        {m?.measurementName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth size="small">
                  <InputLabel>Category *</InputLabel>
                  <Select
                    size="small"
                    label="Category *"
                    value={categorySelected}
                    required
                    onChange={(e) => setCategorySelected(e.target.value)}
                    startAdornment={
                      categoryLoading && <CircularProgress size={20} sx={{ mr: 1 }} />
                    }
                  >
                    {categoryData?.map((category) => (
                      <MenuItem key={category?.categoryId} value={category?.categoryId}>
                        {category?.categoryName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth size="small">
                  <InputLabel>Quarter *</InputLabel>
                  <Select
                    size="small"
                    label="Quarter"
                    value={quarterSelected}
                    onChange={(e) => setQuarterSelected(e.target.value)}
                  >
                    {quarters.map((q) => (
                      <MenuItem key={q.id} value={q.id}>
                        {q.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth size="small">
                  <InputLabel>Year *</InputLabel>
                  <Select
                    size="small"
                    label="Year"
                    value={yearSelected}
                    onChange={(e) => setYearSelected(e.target.value)}
                  >
                    {getYears().map((y) => (
                      <MenuItem key={y} value={y}>
                        {y}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Divider>
                  <Typography color="primary" variant="body2">
                    Optional Selection
                  </Typography>
                </Divider>

                <FormControl fullWidth size="small">
                  <InputLabel>State</InputLabel>
                  <Select
                    size="small"
                    label="State"
                    value={stateSelected}
                    required
                    onChange={(e) => setStateSelected(e.target.value)}
                    startAdornment={stateLoading && <CircularProgress size={20} sx={{ mr: 1 }} />}
                  >
                    {stateList?.map((i) => (
                      <MenuItem key={i?.stateId} value={i?.stateId}>
                        {i?.stateName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            </Box>

            <Divider />

            <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
              <Button
                fullWidth
                variant="contained"
                size="large"
                startIcon={
                  loading ? <CircularProgress size={20} color="inherit" /> : <Visibility />
                }
                onClick={handlePreview}
                disabled={loading}
                sx={{ py: 1.2, textTransform: 'none', fontWeight: 700, borderRadius: 2 }}
              >
                {loading ? 'Generating...' : 'Preview'}
              </Button>
            </Box>
          </Stack>
        </Grid2>
      </Grid2>

      <style>
        {`
    .report-html-container { 
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
      width: 100%; 
      margin: 0 auto; 
      color: #2D3436; 
    }
    
    .report-html-container h2 { 
      text-align: center; 
      color: #1a1a1a; 
      margin-bottom: 12px;
      font-size: 1.75rem; 
      font-weight: 700;
      letter-spacing: -0.02em; 
    }

    .report-html-container .subheading {
      color: #000;
      font-size: 1rem;
      font-weight: 600;
      margin-bottom: 100px;
    }

    .report-html-container table { 
      width: 100% !important; 
      table-layout: fixed; 
      border-collapse: collapse; 
      margin-bottom: 30px; 
      font-size: 11px; 
      background-color: white; 
    }

    .report-html-container th { 
      background-color: #E3F9E5; 
      color: #1B5E20; 
      font-weight: 700; 
      border: 1px solid #B2D8B6; 
      padding: 12px 6px; 
      text-transform: uppercase;
      font-size: 10px;
      letter-spacing: 0.05em;
    }

    .report-html-container td { 
      border: 1px solid #DFE6E9; 
      padding: 10px 6px; 
      text-align: center; 
    }

    .report-html-container tbody td:first-of-type { 
      text-align: left !important; 
      padding-left: 15px; 
      width: 28%; 
      font-weight: 600; 
      color: #2D3436;
    }

    .report-html-container tr:nth-of-type(even) { 
      background-color: #FAFAFA; 
    }

    /* Keep page break logic for the PDF generator */
    .report-html-container div { 
      page-break-after: always; 
      margin-bottom: 60px; 
      width: 100%;
    }
  `}
      </style>
    </Box>
  );
}
